import { ReactNode, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import cn from 'classnames';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Ui
import Text from 'ui3/Text/Text';
import Switch from 'ui3/Switch/Switch';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import SectionDivider from 'components/common3/SectionDivider/SectionDivider';
import WizardStepLayout from 'components/ProfileSetup/components/common/CreateStreamWizard/WizardStepLayout/WizardStepLayout';
import BoutCard from './BoutCard/BoutCard';
import FanEngagementSection from './FanEngagementSection/FanEngagementSection';
import { createEmptyBout, isFilledBout } from './types';
// Styles
import styles from './AdvancedOptionsStep.module.scss';

type AdvancedOptionsStepProps = {
  /** With an aside the step becomes a two-column layout (create wizard); without it, a centered column (Matchcard modal). */
  aside?: ReactNode;
};

const AdvancedOptionsStep = ({
  aside,
}: AdvancedOptionsStepProps): JSX.Element => {
  const { register, watch, setValue } = useFormContext<FormInputs>();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useEffect(() => {
    register({ name: 'bouts' });
  }, [register]);

  const matchcardEnabled = watch('matchcardEnabled');
  const bouts = watch('bouts') || [];
  // Matchups belong to paid streams only (isFree === false); free streams never get a fight card.
  const matchcardAllowed = !watch('isFree');

  useEffect(() => {
    if (matchcardEnabled && bouts.length === 0) {
      setValue('bouts', [createEmptyBout(uuidv4())]);
    }
  }, [matchcardEnabled, bouts.length, setValue]);

  const hasBouts =
    matchcardAllowed && matchcardEnabled && bouts.some(isFilledBout);

  useEffect(() => {
    if (!hasBouts) {
      setValue('fanPollingEnabled', false);
      setValue('sharePickGraphicsEnabled', false);
    }
  }, [hasBouts, setValue]);

  const setBouts = (next: typeof bouts) => {
    setValue('bouts', next);
    if (!hasBouts && next.some(isFilledBout)) {
      setValue('fanPollingEnabled', true);
      setValue('sharePickGraphicsEnabled', true);
    }
  };

  const usedStoreIds = bouts.flatMap((bout) =>
    [bout.competitorA, bout.competitorB]
      .map((competitor) => competitor?.storeId)
      .filter((storeId): storeId is string => Boolean(storeId))
  );

  const handleAddBout = () => {
    setValue('bouts', [...bouts, createEmptyBout(uuidv4())]);
  };

  const handleBoutChange = (index: number, bout: typeof bouts[number]) => {
    setBouts(
      bouts.map((existing, existingIndex) => {
        if (existingIndex === index) {
          return bout;
        }
        return bout.isMainEvent
          ? { ...existing, isMainEvent: false }
          : existing;
      })
    );
  };

  const handleBoutRemove = (index: number) => {
    setValue(
      'bouts',
      bouts.filter((_, existingIndex) => existingIndex !== index)
    );
  };

  const handleBoutReorder = (fromIndex: number, toIndex: number) => {
    // Bounds guard: keyboard reordering can ask for index -1 / length.
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= bouts.length) {
      return;
    }

    const reordered = [...bouts];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setValue('bouts', reordered);
  };

  const clearDragState = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const content = (
    <div className={cn(styles.content, { [styles.withAside]: Boolean(aside) })}>
      <div className={styles.heading}>
        <Text variant="h4">Advanced Options</Text>
        <Text variant="body1Regular16" color="lights-medium">
          Customize your event with additional options like matchups and fan
          engagement.
        </Text>
      </div>

      {matchcardAllowed && (
        <>
          <div className={styles.matchcard}>
            <div className={styles.matchcardHeader}>
              <div className={styles.matchcardHeaderCopy}>
                <Text variant="body1Medium16">Matchcard & Participants</Text>
                <Text variant="body2Regular" color="lights-medium">
                  Add fighters and organize your event.
                </Text>
              </div>
              <Switch name="matchcardEnabled" ref={register} />
            </div>

            {matchcardEnabled && (
              <div className={styles.bouts}>
                {bouts.map((bout, index) => (
                  <BoutCard
                    key={bout.id}
                    bout={bout}
                    index={index}
                    onChange={(updated) => handleBoutChange(index, updated)}
                    onRemove={() => handleBoutRemove(index)}
                    excludeStoreIds={usedStoreIds}
                    isDragging={dragIndex === index}
                    isDropTarget={
                      dropIndex === index &&
                      dragIndex !== null &&
                      dragIndex !== index
                    }
                    onDragStart={setDragIndex}
                    onDragOver={setDropIndex}
                    onDragEnd={clearDragState}
                    onDrop={(toIndex) => {
                      if (dragIndex !== null) {
                        handleBoutReorder(dragIndex, toIndex);
                      }
                      clearDragState();
                    }}
                    onReorder={handleBoutReorder}
                  />
                ))}

                <button
                  type="button"
                  className={styles.addMatchupButton}
                  onClick={handleAddBout}
                >
                  <MillionsIcon name="plus" size={24} />
                  <Text variant="buttonL">Add Another Matchup</Text>
                </button>
              </div>
            )}
          </div>

          <SectionDivider />
        </>
      )}

      <FanEngagementSection disabled={!hasBouts} />
    </div>
  );

  if (aside) {
    return <WizardStepLayout aside={aside}>{content}</WizardStepLayout>;
  }

  return <div className={styles.root}>{content}</div>;
};

export default AdvancedOptionsStep;
